import { Component, Input, AfterContentInit, OnDestroy } from '@angular/core';
import { Condition } from '../models/condition.model';
import { ConditionDialogService } from './condition-dialog.service';
import { CrmEntitiesService } from '../../crm-entities/crm-entities.service';
import {
  CrmEntity,
  CrmEntityAttribute,
  IAttributeOption
} from '../../crm-entities/crm-entities.model';
import {
  CrmAttributeTypeToOperatorsHash,
  CrmEntityAttributeOperators,
} from '../models/crm-entities-attributes-operators';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '../../dialogs/dialog.service';
import { LookupDialogComponent } from '../../lookup-dialog-component/lookup-dialog.component';
import { DialogCloseReason, IDialogOnClose } from '../../dialogs/base-dialog';
import { AbstractControl, FormControl, ValidatorFn, Validators } from '@angular/forms';

import * as moment_ from 'moment';
import { Subscription } from 'rxjs';
import { ILookupData } from '../../lookup-dialog-component';
const moment  = moment_;

/// <reference types="core-js.d.ts" />

enum RightSideElementTypes {
  InputNumber = 1,
  InputText = 2,
  InputMultiLineText = 3,
  InputDecimal = 4,
  InputDropDown = 5,
  DateTimePicker = 6,
  TwoDateTimePickers = 7,
  DateOnlyPicker = 8,
  TwoDateOnlyPickers = 9,
  Lookup = 10
}

export enum ConditionControlType {
  Entity = 1,
  Attribute = 2,
  Operator = 3,
  RightHandSide = 4,
  RightHandSideSecondValue = 5
}

interface IOperatorViewModel {
  displayName: string;
  id: string;
}

@Component({
  selector: 'single-condition',
  templateUrl: 'single-condition.html'
})

export class SingleConditionComponent implements AfterContentInit, OnDestroy {
  @Input() conditionModel: Condition;

  readonly maxInteger: number = 2147483647;
  readonly minInteger: number = -2147483648;
  readonly absDecimal: number = 922337203685477.000;

  checked: boolean = false;
  conditionNumber: number;
  entityTypeControl: FormControl;
  attributeControl: FormControl;
  operatorControl: FormControl;
  rightHandSideControl: FormControl;
  rightHandSideSecondValueControl: FormControl;
  selectedCrmEntity: CrmEntity = null;
  selectedCrmEntityAttribute: CrmEntityAttribute = null;
  selectedEntitySortedAttributesList: CrmEntityAttribute[];
  selectedAttributeListOption: string = null;
  operatorRightHandSideIsNeeded: boolean;
  operatorsList: IOperatorViewModel[];
  crmAttributeValid: boolean = true;
  crmAttributeOptionsValid: boolean = true;
  attributeControlOldValue: string = null;

  RightSideElementTypes = RightSideElementTypes;
  rightSideElementToShow: RightSideElementTypes;

  date: Date = moment().seconds(0).milliseconds(0).toDate();
  date2: Date = moment().seconds(0).milliseconds(0).toDate();
  dateTime: Date = moment().seconds(0).milliseconds(0).toDate();
  dateTime2: Date = moment().seconds(0).milliseconds(0).toDate();

  checkValidityEventSubscription: Subscription;

  decimalStep: string; // this is needed only when a Decimal input is the right-hand-side

  dateOnlyPickerSettings = {
    bigBanner: false,
    timePicker: false,
    format: 'MM-dd-yyyy',
    inputFormat: 'DD-MMM-YYYY',
    defaultOpen: false,
    closeOnSelect: true
  };

  dateTimePickerSettings = {
    bigBanner: false,
    timePicker: true,
    format: 'dd-MMM-yyyy HH:mm',
    inputFormat: 'DD-MMM-YYYY hh:mm A',
    defaultOpen: false,
    closeOnSelect: true
  };

  constructor(private conditionDialogService: ConditionDialogService,
              private translate: TranslateService,
              private dialogService: DialogService,
              public crmEntitiesService: CrmEntitiesService) {

  }

  ngAfterContentInit(): void {
    this.conditionNumber = this.conditionDialogService.getConditionNumberFromGenerator();
    this.checkValidityEventSubscription = this.conditionDialogService.doCheckValidity.subscribe(() => { this.checkListOptionsValidity(); });
    this.addConditionControlsToGroup();
    if (this.conditionModel.entityType) {
      this.setEntity();
      if (this.conditionModel.attributeLogicalName) {
        this.prepareOperatorsViewModel();

        if (this.conditionModel.operatorId) {
          this.setOperatorRightHandSideViewModel();
          this.initSpecificRightHandSideModelAndViewModel();
        }
      }
    }
  }

  checkListOptionsValidity() {
    if (this.conditionModel.attributeLogicalName) {
      if (this.conditionModel && this.conditionModel.attributeLogicalName) {
        this.crmAttributeValid = this.conditionModel.checkValidity(this.crmEntitiesService.entities);
        if (!this.crmAttributeValid) {
          this.conditionDialogService.addInvalidCondition(this.conditionNumber);
        }
        if (this.conditionModel.attributeType === 'List') {
          this.crmAttributeOptionsValid = this.conditionModel.checkListOptionsValidity(this.crmEntitiesService.entities);
          if (!this.crmAttributeOptionsValid) {
            this.conditionDialogService.addInvalidCondition(this.conditionNumber);
          }
        }
      }
    }
  }

  removeAllControlsFromGroup() {
    this.conditionDialogService.removeControlFromGroup(this.getControlName(ConditionControlType.Entity));
    this.conditionDialogService.removeControlFromGroup(this.getControlName(ConditionControlType.Attribute));
    this.conditionDialogService.removeControlFromGroup(this.getControlName(ConditionControlType.Operator));
    this.conditionDialogService.removeControlFromGroup(this.getControlName(ConditionControlType.RightHandSide));
    this.conditionDialogService.removeControlFromGroup(this.getControlName(ConditionControlType.RightHandSideSecondValue));
    this.conditionDialogService.conditionsForm.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.removeAllControlsFromGroup();
    this.conditionDialogService.removeInvalidCondition(this.conditionNumber);
    this.checkValidityEventSubscription.unsubscribe();
  }

  addConditionControlsToGroup() {
    const attributeControlName = this.getControlName(ConditionControlType.Attribute);

    this.entityTypeControl = new FormControl(this.conditionModel.entityType, [Validators.required]);
    this.conditionDialogService.addControlToGroup(this.entityTypeControl, this.getControlName(ConditionControlType.Entity));

    this.attributeControl = new FormControl(this.conditionModel.attributeLogicalName, [Validators.required]);
    this.conditionDialogService.addControlToGroup(this.attributeControl, attributeControlName);

    this.operatorControl = new FormControl(this.conditionModel.operatorId, [Validators.required]);
    this.conditionDialogService.addControlToGroup(this.operatorControl, this.getControlName(ConditionControlType.Operator));
  }

  onEntityChanged(newValue: any) {
    if (newValue !== this.entityTypeControl.value) {
      // clear all the previous data if it was selected
      this.conditionModel.operatorId = null;
      this.conditionModel.rightHandSide = null;
      this.conditionModel.attributeLogicalName = null;
      this.conditionModel.attributeDisplayName = null;
      this.entityTypeControl.setValue(newValue);
      this.setEntity();
    }
  }

  onAttributeChanged(newValue: any) {
    if (newValue !== this.attributeControlOldValue) {
      this.crmAttributeOptionsValid = true;
      this.crmAttributeValid = true;
      this.conditionDialogService.removeInvalidCondition(this.conditionNumber);

      // this should be here since once a new attribute is selected this should be first thing we clear. old operators
      this.conditionModel.operatorId = null;
      this.conditionModel.rightHandSide = null;
      this.attributeControlOldValue = newValue;
    }

    this.prepareOperatorsViewModel();

    // attribute deleted from CRM
    if (!this.selectedCrmEntityAttribute) {
      return;
    }

    // save additional properties of the attribute so we could be able to show Human readable errors
    this.conditionModel.attributeDisplayName = this.selectedCrmEntityAttribute.name;
    this.conditionModel.attributeType = this.selectedCrmEntityAttribute.type;
  }

  getControlName(conditionControlType: ConditionControlType) {
    const controlName: string = ConditionControlType[conditionControlType].toString();
    return `${controlName}${this.conditionNumber}`;
  }

  addConditionValuesControlsToGroup() {
    let operatorRightHandSideSecondValueIsNeeded: boolean = false;
    const rightHandSideControlName = this.getControlName(ConditionControlType.RightHandSide);
    const rightHandSideSecondValueControlName = this.getControlName(ConditionControlType.RightHandSideSecondValue);

    this.conditionDialogService.removeControlFromGroup(rightHandSideControlName);
    this.conditionDialogService.removeControlFromGroup(rightHandSideSecondValueControlName);

    this.conditionDialogService.conditionsForm.updateValueAndValidity();

    if (this.rightSideElementToShow === RightSideElementTypes.InputDropDown) {
      this.rightHandSideControl = new FormControl(this.selectedAttributeListOption, [Validators.required]);
    } else if (this.rightSideElementToShow === RightSideElementTypes.DateTimePicker) {
      this.rightHandSideControl = new FormControl(this.dateTime, [Validators.required]);
    } else if (this.rightSideElementToShow === RightSideElementTypes.TwoDateTimePickers) {
      operatorRightHandSideSecondValueIsNeeded = true;
      this.rightHandSideControl = new FormControl(
        this.dateTime,
        [Validators.required,
          this.datesBetweenValidator(rightHandSideControlName, rightHandSideSecondValueControlName)]
      );
      this.rightHandSideSecondValueControl = new FormControl(this.dateTime2, [Validators.required]);
    } else if (this.rightSideElementToShow === RightSideElementTypes.DateOnlyPicker) {
      this.rightHandSideControl = new FormControl(this.date, [Validators.required]);
    } else if (this.rightSideElementToShow === RightSideElementTypes.TwoDateOnlyPickers) {
      operatorRightHandSideSecondValueIsNeeded = true;
      this.rightHandSideControl = new FormControl(
        this.date,
        [Validators.required, this.datesBetweenValidator(rightHandSideControlName, rightHandSideSecondValueControlName)]
      );
      this.rightHandSideSecondValueControl = new FormControl(this.date2, [Validators.required]);
    } else if (this.rightSideElementToShow === RightSideElementTypes.InputNumber) {
      this.rightHandSideControl = new FormControl(this.conditionModel.rightHandSide, [Validators.required, this.maxAllowedIntegerValidator(rightHandSideControlName)]);
    } else if (this.rightSideElementToShow === RightSideElementTypes.InputDecimal) {
      this.rightHandSideControl = new FormControl(this.conditionModel.rightHandSide, [Validators.required, this.maxAllowedDecimalValidator(rightHandSideControlName)]);
    } else if (this.rightSideElementToShow === RightSideElementTypes.InputText || this.rightSideElementToShow === RightSideElementTypes.InputMultiLineText) {
      const validators = [Validators.required];
      if (this.selectedCrmEntityAttribute.maxLength > 0) {
        validators.push(Validators.maxLength(this.selectedCrmEntityAttribute.maxLength));
      }
      this.rightHandSideControl = new FormControl(this.conditionModel.rightHandSide, validators);
    } else {
      this.rightHandSideControl = new FormControl(this.conditionModel.rightHandSide, [Validators.required]);
    }

    if (this.operatorRightHandSideIsNeeded) {
      this.conditionDialogService.addControlToGroup(this.rightHandSideControl, rightHandSideControlName);

      if (operatorRightHandSideSecondValueIsNeeded) {
        this.conditionDialogService.addControlToGroup(this.rightHandSideSecondValueControl, rightHandSideSecondValueControlName);
      }
    }
  }

  datesBetweenValidator(fromDateControlKey: string, toDateControlKey: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      const fromDateControl = this.conditionDialogService.conditionsForm.controls[fromDateControlKey];
      const toDateControl = this.conditionDialogService.conditionsForm.controls[toDateControlKey];
      let firstDateIsGreater: boolean = false;

      if (fromDateControl && toDateControl && fromDateControl.value && toDateControl.value) {
        const fromDateTicks = fromDateControl.value.getTime();
        const toDateTicks = toDateControl.value.getTime();
        if (fromDateTicks > toDateTicks) {
          firstDateIsGreater = true;
        }
      }
      return firstDateIsGreater ? {'firstDateIsGreater': {value: true}} : null;
    };
  }

  maxAllowedIntegerValidator(intControlKey: string): ValidatorFn {
    return (): { [key: string]: any } => {
      const numberControl = this.conditionDialogService.conditionsForm.controls[intControlKey];
      let numberIsGreater: boolean = false;
      if (numberControl && numberControl.value) {
        numberIsGreater = !(/^-?[0-9]+$/.test(numberControl.value)) || numberControl.value < this.minInteger || this.maxInteger < numberControl.value;
      }
      return numberIsGreater ? {'numberIsGreater': {value: true}} : null;
    };
  }

  maxAllowedDecimalValidator(intControlKey: string): ValidatorFn {
    return (): { [key: string]: any } => {
      const decimalControl = this.conditionDialogService.conditionsForm.controls[intControlKey];
      let decimalIsGreater: boolean = false;
      if (decimalControl && decimalControl.value) {
        decimalIsGreater = !(/^-?[0-9]+(\.[0-9]{1,2})?$/.test(decimalControl.value)) || this.absDecimal < Math.abs(decimalControl.value);
      }
      return decimalIsGreater ? {'decimalIsGreater': {value: true}} : null;
    };
  }

  onOperatorChanged(newValue: any) {
    if (newValue !== this.operatorControl.value) {
      this.conditionModel.rightHandSide = null;
    }

    this.setOperatorRightHandSideViewModel();
    this.initSpecificRightHandSideModelAndViewModel();
    this.addConditionValuesControlsToGroup();
  }

  onConditionCheckedToggle(event: any) {
    if (this.checked) {
      this.conditionDialogService.removeConditionFromSelected(this.conditionModel);
    } else {
      this.conditionDialogService.addConditionToSelected(this.conditionModel);
    }

    this.checked = !this.checked;
  }

  public onDateSelected(event: any) {
    const date = new Date(event);
    this.conditionModel.rightHandSide = this.formatDateOnly(date);
  }

  public onDateTimeSelected(date: Date) {
    if (this.selectedCrmEntityAttribute.type === 'DateTimeZoneIndependent') {
      // we save the date and time on the right hand side without the Z since this is the value that will
      // be rendered in free marker and the syntax ?datetime.iso does not need the Z part to know that this is zone independant
      this.conditionModel.rightHandSide = this.formatDateTimeZoneIndependent(date);
    } else {
      this.conditionModel.rightHandSide = this.formatDateTime(date);
    }
  }

  public onBetweenDateTime1Selected(date: Date) {
    if (this.selectedCrmEntityAttribute.type === 'DateTimeZoneIndependent') {
      this.conditionModel.rightHandSide = this.buildRightHandSideForBetweenDateTimeZoneIndependent(date, this.dateTime2);
    } else {
      this.conditionModel.rightHandSide = this.buildRightHandSideForBetweenDateTime(date, this.dateTime2);
    }
  }

  public onBetweenDateTime2Selected(date: Date) {
    if (this.selectedCrmEntityAttribute.type === 'DateTimeZoneIndependent') {
      this.conditionModel.rightHandSide = this.buildRightHandSideForBetweenDateTimeZoneIndependent(this.dateTime, date);
    } else {
      this.conditionModel.rightHandSide = this.buildRightHandSideForBetweenDateTime(this.dateTime, date);
    }

    this.rightHandSideControl.markAsDirty();

    setTimeout(() => {
      this.rightHandSideControl.updateValueAndValidity();
    }, 0);

  }

  public onBetweenDate1Selected(date: Date) {
    this.conditionModel.rightHandSide = this.buildRightHandSideForBetweenDateOnly(date, this.date2);
  }

  public onBetweenDate2Selected(date: Date) {
    this.conditionModel.rightHandSide = this.buildRightHandSideForBetweenDateOnly(this.date, date);
    this.rightHandSideControl.markAsDirty();

    setTimeout(() => {
      this.rightHandSideControl.updateValueAndValidity();
    }, 0);
  }

  onSelectionOnListAttributeChanged(valueSelected: any) {
    if (valueSelected !== this.rightHandSideControl.value) {
      this.crmAttributeOptionsValid = true;
      this.crmAttributeValid = true;
      this.conditionDialogService.removeInvalidCondition(this.conditionNumber);
    }

    // create a clone of the option and place it on the model (detach from the list UI control)
    const foundOption: IAttributeOption = this.selectedCrmEntityAttribute.options.find((option) => {
      return (option.value === valueSelected);
    });

    if (foundOption) {
      this.conditionModel.rightHandSide = this.cloneListOption(foundOption);
    } else {
      this.conditionModel.rightHandSide = null;
    }
  }

  public openLookupDialog() {
    this.dialogService.createDialog<LookupDialogComponent>(LookupDialogComponent, {
      entityTypes: this.selectedCrmEntityAttribute.lookupEntityIds
    }).onClose.subscribe((result: IDialogOnClose) => {
      if (result.reason === DialogCloseReason.OK) {
        const selectedRecords = result.data.selected;
        const fieldList = result.data.fieldList;

        // TODO: check if Name exists on all the fieldsList
        this.conditionModel.rightHandSide = {
          name: selectedRecords[0].Name,
          id: selectedRecords[0].Id
        } as ILookupData;
      }
    });
  }

  private cloneListOption(listOPtion: IAttributeOption) {
    return ({
      text: listOPtion.text,
      value: listOPtion.value
    } as IAttributeOption);
  }

  private buildRightHandSideForBetweenDateTime(date1: Date, date2: Date) {
    return {
      gteValue: this.formatDateTime(date1),
      lteValue: this.formatDateTime(date2)
    };
  }

  private buildRightHandSideForBetweenDateTimeZoneIndependent(date1: Date, date2: Date) {
    return {
      gteValue: this.formatDateTimeZoneIndependent(date1),
      lteValue: this.formatDateTimeZoneIndependent(date2)
    };
  }

  private buildRightHandSideForBetweenDateOnly(date1: Date, date2: Date) {
    return {
      gteValue: this.formatDateOnly(date1),
      lteValue: this.formatDateOnly(date2)
    };
  }

  private formatDateTime(date: Date) {
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date.toISOString();
  }

  private formatDateTimeZoneIndependent(date: Date) {
    const dateAsMoment = moment(date);
    return dateAsMoment.format('YYYY-MM-DDTHH:mm:ss');
  }

  private formatDateOnly(date: Date) {
    const dateAsMoment = moment(date);
    // this will extract the date part from the date object and will create a new moment object with the date and time as 12:00 and utc
    // like the crm saves these values on date only
    return dateAsMoment.format('YYYY-MM-DDT00:00:00');
  }

  private setEntity() {
    if (!this.conditionModel.entityType) {
      // the user could select the empty placeholder. clear also the entity name
      this.conditionModel.entityName = null;
      return;
    }

    this.selectedCrmEntity = this.crmEntitiesService.getEntityByType(this.conditionModel.entityType);
    this.conditionModel.entityName = this.selectedCrmEntity.entityName;

    this.selectedEntitySortedAttributesList = this.selectedCrmEntity.attributes.sort((a: CrmEntityAttribute, b: CrmEntityAttribute) => {

      if (a.name.toLowerCase() > b.name.toLowerCase()) {
        return 1;
      } else if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return -1;
      }
      return 0;
    });
  }

  private setOperatorRightHandSideViewModel() {

    // attribute deleted from CRM
    if (!this.selectedCrmEntityAttribute) {
      return;
    }

    switch (this.selectedCrmEntityAttribute.type) {
      case 'Boolean':
      case 'List':
        this.rightSideElementToShow = RightSideElementTypes.InputDropDown;
        break;
      case 'TextArea':
        this.rightSideElementToShow = RightSideElementTypes.InputMultiLineText;
        break;
      case 'Text':
        this.rightSideElementToShow = RightSideElementTypes.InputText;
        break;
      case 'Money':
      case 'Decimal':
        this.rightSideElementToShow = RightSideElementTypes.InputDecimal;
        if (this.selectedCrmEntityAttribute.accuracy > 0) {
          this.decimalStep = '.' + '1'.padStart(this.selectedCrmEntityAttribute.accuracy, '0');
        } else {
          this.decimalStep = '';
        }
        break;
      case 'Integer':
        this.rightSideElementToShow = RightSideElementTypes.InputNumber;
        break;

      case 'DateTimeZoneIndependent':
      case 'DateTime':
        if (this.conditionModel.operatorId === 'betweenDateTime') {
          this.rightSideElementToShow = RightSideElementTypes.TwoDateTimePickers;
        } else {
          this.rightSideElementToShow = RightSideElementTypes.DateTimePicker;
        }
        break;

      case 'DateOnly':
        if (this.conditionModel.operatorId === 'betweenDateOnly') {
          this.rightSideElementToShow = RightSideElementTypes.TwoDateOnlyPickers;
        } else {
          this.rightSideElementToShow = RightSideElementTypes.DateOnlyPicker;
        }
        break;
      case 'Lookup':
        this.rightSideElementToShow = RightSideElementTypes.Lookup;
        break;
    }

    // on some operators the right hand side is not needed check for this.
    this.operatorRightHandSideIsNeeded = (
      (this.conditionModel.operatorId !== 'containsData') &&
      (this.conditionModel.operatorId !== 'doesNotContainData')
    );
  }

  private triggerAttributeControlValidation() {
    this.attributeControl.markAsDirty();
    this.attributeControl.markAsTouched();

    setTimeout(() => {
      this.attributeControl.updateValueAndValidity();
    }, 0);
  }

  private prepareOperatorsViewModel() {
    this.operatorsList = [];

    this.selectedCrmEntityAttribute = this.selectedCrmEntity.attributes.find(
      attribute => attribute.logicalName === this.conditionModel.attributeLogicalName);

    // attribute not found, deleted from CRM
    if (!this.selectedCrmEntityAttribute) {
      this.conditionModel.attributeLogicalName = null;
      this.conditionModel.operatorId = null;
      this.conditionModel.rightHandSide = null;
      return;
    }

    const tempOperatorsList = CrmAttributeTypeToOperatorsHash[this.selectedCrmEntityAttribute.type];

    tempOperatorsList.forEach((operatorId: string) => {
      const operator = CrmEntityAttributeOperators[operatorId];

      const operatorViewModel: IOperatorViewModel = {
        displayName: this.translate.instant(operator.displayNameKey),
        id: operator.name
      };

      this.operatorsList.push(operatorViewModel);
    });

    if (this.operatorsList.length === 1) {
      // there is only one option select that operator for the user
      this.conditionModel.operatorId = this.operatorsList[0].id;
      // in this case continue to the next stage and prepare the right-hand-side view
      // model since we already selected the operator for the user.
      this.setOperatorRightHandSideViewModel();

      this.initSpecificRightHandSideModelAndViewModel();
    }
  }

  private initSpecificRightHandSideModelAndViewModel() {
    // for some types (date and DateTime for now) a view model needs to be created
    switch (this.rightSideElementToShow) {
      case RightSideElementTypes.InputDropDown:
        if (this.conditionModel.rightHandSide && (this.selectedCrmEntityAttribute.type === 'Boolean' ||
          this.selectedCrmEntityAttribute.type === 'List')) {

          // find the right value that should be selected in the select and set it up on the list view-model
          const foundOption: IAttributeOption = this.selectedCrmEntityAttribute.options.find((option) => {
            return (option.value === (this.conditionModel.rightHandSide as IAttributeOption).value);
          });
          if (foundOption) {
            this.selectedAttributeListOption = foundOption.value;
          } else {
            // just a default if the user has deleted from the crm one of the options we set it to null.
            this.selectedAttributeListOption = null;
          }
        } else if (!this.conditionModel.rightHandSide && this.selectedCrmEntityAttribute.type === 'Boolean') {
          // select the first item as a default since it's boolean
          this.conditionModel.rightHandSide = this.cloneListOption(this.selectedCrmEntityAttribute.options[0]);
          this.selectedAttributeListOption = this.selectedCrmEntityAttribute.options[0].value;
        } else {
          // this happens if there is nothing on the right hand side on the model
          this.selectedAttributeListOption = null;
        }
        break;
      case RightSideElementTypes.DateTimePicker:
        this.dateTime = this.setDateAccordingToRightHandSide(this.conditionModel.rightHandSide);

        if (!this.conditionModel.rightHandSide) {
          if (this.selectedCrmEntityAttribute.type === 'DateTimeZoneIndependent') {
            this.conditionModel.rightHandSide = this.formatDateTimeZoneIndependent(this.dateTime);
          } else {
            this.conditionModel.rightHandSide = this.formatDateTime(this.dateTime);
          }
        }
        break;
      case RightSideElementTypes.DateOnlyPicker:
        this.date = this.setDateAccordingToRightHandSide(this.conditionModel.rightHandSide);

        if (!this.conditionModel.rightHandSide) {
          this.conditionModel.rightHandSide = this.formatDateOnly(this.date);
        }
        break;
      case RightSideElementTypes.TwoDateTimePickers:
        if (this.conditionModel.rightHandSide) {
          this.dateTime = this.setDateAccordingToRightHandSide(this.conditionModel.rightHandSide.gteValue);
          this.dateTime2 = this.setDateAccordingToRightHandSide(this.conditionModel.rightHandSide.lteValue);
        }

        if (!this.conditionModel.rightHandSide) {
          if (this.selectedCrmEntityAttribute.type === 'DateTimeZoneIndependent') {
            this.conditionModel.rightHandSide = this.buildRightHandSideForBetweenDateTimeZoneIndependent(this.dateTime, this.dateTime2);
          } else {
            this.conditionModel.rightHandSide = this.buildRightHandSideForBetweenDateTime(this.dateTime, this.dateTime2);
          }
        }

        break;
      case RightSideElementTypes.TwoDateOnlyPickers:
        if (this.conditionModel.rightHandSide) {
          this.date = this.setDateAccordingToRightHandSide(this.conditionModel.rightHandSide.gteValue);
          this.date2 = this.setDateAccordingToRightHandSide(this.conditionModel.rightHandSide.lteValue);
        }

        if (!this.conditionModel.rightHandSide) {
          this.conditionModel.rightHandSide = this.buildRightHandSideForBetweenDateOnly(this.date, this.date2);
        }
        break;
    }
  }

  private setDateAccordingToRightHandSide(rightHandSide: string) {
    if (rightHandSide) {
      return new Date(rightHandSide);
    }

    return moment().seconds(0).milliseconds(0).toDate();
  }

}
